import { NgStyle, NgTemplateOutlet } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import {
    FontWeight,
    PaddingSize,
    PxSize,
    RemSize,
    Size,
} from 'shared/design-system/data-access/layout';
import { ColorEnum } from 'shared/design-system/data-access/theme';

type TextOverflow = 'wrap' | 'ellipsis' | 'pre-line';

@Component({
    selector: 'it-text',
    imports: [NgStyle, NgTemplateOutlet],
    templateUrl: './text.component.html',
    styleUrls: ['./text.component.scss'],
})
export class TextComponent {
    @Input() weight?: FontWeight;
    @Input() color?: ColorEnum;
    @Input() fontSize?: RemSize | PxSize;
    @Input() lineHeight?: string;
    @Input() underline?: boolean;
    @Input() italic?: boolean;
    @Input() opacity?: Size;
    @Input() align?: 'center' | 'end';
    @Input() ellipsis?: boolean;
    @Input() overflow?: TextOverflow;
    @Input() padding?: PaddingSize;
    @Input() textTransform?: 'uppercase' | 'lowercase' | 'capitalize' | 'none';
    @Input() breakWord?: boolean;
    @Input() pointer = false;

    spanMode = !!inject(TextComponent, {
        skipSelf: true,
        optional: true,
    });

    get style() {
        const baseStyle = {
            fontWeight: this.weight,
            color: this.color,
            textAlign: this.align,
            fontSize: this.fontSize,
            lineHeight: this.lineHeight,
            opacity: this.opacity,
            overflowWrap: this.breakWord ? 'break-word' : 'anywhere',
            textDecoration: this.underline ? 'underline' : undefined,
            fontStyle: this.italic ? 'italic' : undefined,
            textTransform: this.textTransform ?? 'none',
            cursor: this.pointer ? 'pointer' : undefined,
            padding: this.padding,
        };

        // Support legacy ellipsis property for backward compatibility
        if (this.ellipsis || this.overflow === 'ellipsis') {
            return {
                ...baseStyle,
                maxWidth: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            };
        }

        if (this.overflow === 'wrap') {
            return {
                ...baseStyle,
                whiteSpace: 'normal',
                wordWrap: 'break-word',
            };
        }

        if (this.overflow === 'pre-line') {
            return {
                ...baseStyle,
                whiteSpace: 'pre-line',
                wordWrap: 'break-word',
            };
        }

        return baseStyle;
    }
}
